/**
 * assistant-ui runtime for Ask Addy v2.
 *
 * useLocalRuntime drives the chat; the adapter below turns v2's server-sent events into
 * assistant-ui message parts. v2 keeps the conversation itself (thread memory keyed by loan),
 * so each run sends only the newest user message — never the whole history.
 *
 * Approvals use assistant-ui's approval gate: a paused write becomes a tool-call part with
 * `approval: { id }`, the run ends with `requires-action`, the card calls respondToApproval,
 * and the runtime calls run() again on the same message — which then resumes the v2 run.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocalRuntime } from "@assistant-ui/react";
import AskAddyV2, { defaultThreadId } from "../models/askAddyV2";
import { isFirstLegacyFollowUp, stripEarlierChat, withEarlierChat } from "./legacyChats";

/**
 * The server appends these to a stored message that carried attachments (see v2 query-stream):
 * one for files put in the sandbox, one for PDFs and images the model read.
 */
const ATTACHMENT_TAG = /\n\n\[Attached by the loan officer(?:, now in the sandbox)?: ([^\]]+)\]$/;

/**
 * What the LO can attach. Worksheets, Word documents and data files go to v2's sandbox; PDFs and images to
 * the model too. Must match v2's UPLOAD_EXTENSIONS (server/uploads.ts): Word, .ods and TIFF scans were
 * accepted by v2 but couldn't be picked here.
 */
export const ATTACHMENT_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.gif,.tif,.tiff,.bmp,.docx,.doc,.rtf,.odt,.docm,.dotx,.xlsx,.xlsm,.xls,.ods,.csv,.xml";

/** Split a stored message into what the LO typed and the names of the files they attached. */
export function splitAttachmentTags(text) {
  let body = text || "";
  const names = [];
  for (let tag = body.match(ATTACHMENT_TAG); tag; tag = body.match(ATTACHMENT_TAG)) {
    names.unshift(...tag[1].split(", ").map((path) => path.split("/").pop()));
    body = body.slice(0, tag.index);
  }
  return { body, names };
}

/** What v2 saves when the loan officer stops an answer (see STOPPED_NOTE in v2's routes). */
const STOPPED_NOTE = /^\[Stopped by the loan officer before this answer finished\./;

/**
 * First line of the message a scheduled task's run sends the agent: `[Scheduled task · "Credit
 * watch" · Every 15 minutes · …]`. Older runs used `[Autopilot · heartbeat "Credit watch" · …]`.
 */
const SCHEDULED_HEADER = /^\[(?:Scheduled task|Autopilot) · [^\]\n]+\]/;

/** A line that is only a scheduled check's "nothing new" token (v2's QUIET_REPLY): never shown. */
const QUIET_LINE = /^\s*[*`_]*HEARTBEAT_OK[*`_]*[.!]?\s*$/i;
export const withoutQuietToken = (text) =>
  (text || "")
    .split("\n")
    .filter((line) => !QUIET_LINE.test(line))
    .join("\n")
    .trim();

/** The task's name and when it runs, from that header ("" when the header has no name). */
export function parseScheduledHeader(text) {
  const header = (text || "").match(SCHEDULED_HEADER);
  if (!header) return null;
  const named = header[0].match(/"(.+?)" · ([^·\]]+)/);
  const timing = named ? named[2].trim() : "";
  // The legacy header's second field can be "woken by changes on this loan"; show it in plain words.
  const when = /^woken by/.test(timing) ? "this loan changed" : timing;
  return { name: named ? named[1] : "", when };
}

/**
 * What a browser task did, by gate id: set by the browser card before it answers its gate, read by
 * the adapter to resume the paused use_my_browser call with it.
 */
const browserResults = new Map();
export const setBrowserResult = (gateId, result) => browserResults.set(gateId, result);

/** Fired on window after each run, so lists of conversations (titles, order) can refresh. */
export const THREAD_UPDATED_EVENT = "askaddy-v2-thread-updated";

/**
 * Fold v2 events into assistant-ui parts. Pure: (state, event) -> state, so it can be tested
 * without React. `parts` holds finished parts; `text` is the text still streaming.
 */
export function initialFold() {
  return { parts: [], text: "", byToolCall: {}, paused: null, error: null, status: "", approvals: 0 };
}

export function foldEvent(state, event) {
  const s = { ...state, parts: [...state.parts], byToolCall: { ...state.byToolCall } };
  const flushText = () => {
    if (s.text) {
      s.parts.push({ type: "text", text: s.text });
      s.text = "";
    }
  };

  switch (event.type) {
    case "chunk":
      s.text += event.content || "";
      return s;

    case "status":
      s.status = event.message || "";
      return s;

    case "reasoning": {
      // The model's own reasoning, when it streams any: shown inside the thinking trace.
      if (!event.content) return s;
      const last = s.parts[s.parts.length - 1];
      if (!s.text && last && last.type === "reasoning") {
        s.parts[s.parts.length - 1] = { ...last, text: last.text + event.content };
      } else {
        flushText();
        s.parts.push({ type: "reasoning", text: event.content });
      }
      return s;
    }

    case "tool_call": {
      flushText();
      s.byToolCall[event.toolCallId] = s.parts.length;
      s.parts.push({
        type: "tool-call",
        toolCallId: event.toolCallId,
        toolName: event.toolName,
        // The server's plain-words label for this step, and whether to list it at all.
        ...(event.label ? { label: event.label } : {}),
        ...(event.hidden ? { hidden: true } : {}),
        args: event.args || {},
        argsText: JSON.stringify(event.args || {}),
      });
      return s;
    }

    case "tool_result": {
      const i = s.byToolCall[event.toolCallId];
      if (i === undefined) return s; // a result for a part from an earlier run; nothing to update here
      s.parts[i] = { ...s.parts[i], result: event.result ?? null, isError: Boolean(event.isError), subTools: event.subTools };
      return s;
    }

    case "sub_step": {
      // A step inside a specialist's run, streamed live: attach it to that specialist's step
      // (the running one, if the same specialist was asked more than once).
      const owner = `agent-${event.agent}`;
      let i = -1;
      for (let k = s.parts.length - 1; k >= 0; k--) {
        const p = s.parts[k];
        if (p.type !== "tool-call" || p.toolName !== owner) continue;
        if (p.result === undefined) {
          i = k;
          break;
        }
        if (i < 0) i = k;
      }
      if (i < 0) return s;
      const part = s.parts[i];
      const subSteps = [...(part.artifact?.subSteps || [])];
      const j = subSteps.findIndex((x) => x.id === event.id);
      const prev = j >= 0 ? subSteps[j] : { id: event.id, toolName: event.toolName, args: event.args };
      const next = {
        ...prev,
        status: event.phase === "done" ? (event.isError ? "error" : "complete") : "running",
        ...(event.summary ? { summary: event.summary } : {}),
      };
      if (j >= 0) subSteps[j] = next;
      else subSteps.push(next);
      s.parts[i] = { ...part, artifact: { ...(part.artifact || {}), subSteps } };
      return s;
    }

    case "approval_request": {
      flushText();
      // v2 reports a nested approval (inside a delegation) under the PARENT tool call's id, so
      // several approvals can share one id. Each gate gets its own part id; the real ids are kept
      // to resume the v2 run.
      s.approvals += 1;
      const gateId = `${event.toolCallId}~approval~${Date.now()}~${s.approvals}`;
      // A direct write announced its own tool call a moment ago: the card replaces that step.
      // A nested approval (inside a VM delegation) keeps its parent step, which has another name.
      const announced = s.byToolCall[event.toolCallId];
      if (announced !== undefined && s.parts[announced]?.toolName === event.toolName && s.parts[announced].result === undefined) {
        s.parts.splice(announced, 1);
        delete s.byToolCall[event.toolCallId];
        for (const [id, i] of Object.entries(s.byToolCall)) if (i > announced) s.byToolCall[id] = i - 1;
      }
      s.parts.push({
        type: "tool-call",
        toolCallId: gateId,
        toolName: event.toolName,
        args: event.args || {},
        argsText: JSON.stringify(event.args || {}),
        // The card in plain words from the server: { title, fields: [{ label, value }] }.
        ...(event.display ? { display: event.display } : {}),
        approval: { id: gateId },
      });
      s.paused = { runId: event.runId, toolCallId: event.toolCallId, threadId: event.threadId, gateId };
      return s;
    }

    case "clarification": {
      // A tool paused itself for the sidebar. Today that's use_my_browser: the LO allows it, the
      // sidebar runs it in their browser, and the run resumes with the result. It rides on the
      // same gate as approvals (requires-action), so the runtime resumes it the same way.
      if (event.toolName !== "use_my_browser") return s;
      flushText();
      s.approvals += 1;
      const gateId = `${event.toolCallId}~browser~${Date.now()}~${s.approvals}`;
      const announced = s.byToolCall[event.toolCallId];
      if (announced !== undefined && s.parts[announced]?.toolName === event.toolName && s.parts[announced].result === undefined) {
        s.parts.splice(announced, 1);
        delete s.byToolCall[event.toolCallId];
        for (const [id, i] of Object.entries(s.byToolCall)) if (i > announced) s.byToolCall[id] = i - 1;
      }
      s.parts.push({
        type: "tool-call",
        toolCallId: gateId,
        toolName: event.toolName,
        args: event.payload || {},
        argsText: JSON.stringify(event.payload || {}),
        approval: { id: gateId },
      });
      s.paused = { kind: "resume", runId: event.runId, toolCallId: event.toolCallId, threadId: event.threadId, gateId };
      return s;
    }

    case "file":
      flushText();
      s.parts.push({
        type: "data",
        name: "v2-file",
        data: {
          fileId: event.fileId,
          fileName: event.fileName,
          label: event.label,
          sizeBytes: event.sizeBytes,
          url: AskAddyV2.absoluteUrl(event.url),
        },
      });
      return s;

    case "live_view":
      if (s.parts.some((p) => p.type === "data" && p.name === "v2-live-view" && p.data.url === event.url)) return s;
      flushText();
      s.parts.push({ type: "data", name: "v2-live-view", data: { url: event.url } });
      return s;

    case "sources": {
      // Numbered loan documents the answer cites; one list per answer, merged by number.
      const i = s.parts.findIndex((p) => p.type === "data" && p.name === "v2-sources");
      const merged = new Map((i >= 0 ? s.parts[i].data.sources : []).map((x) => [x.n, x]));
      for (const x of event.sources || []) merged.set(x.n, x);
      const part = { type: "data", name: "v2-sources", data: { sources: [...merged.values()].sort((a, b) => a.n - b.n) } };
      if (i >= 0) s.parts[i] = part;
      else {
        flushText();
        s.parts.push(part);
      }
      return s;
    }

    case "choice":
      flushText();
      s.parts.push({ type: "data", name: "v2-choice", data: { question: event.question, options: event.options || [] } });
      return s;

    case "scheduled_task":
      flushText();
      s.parts.push({ type: "data", name: "v2-scheduled-task", data: { id: event.id, name: event.name, when: event.when, nextRunAt: event.nextRunAt || null } });
      return s;

    case "refresh_loan":
      // A write succeeded: the loan page behind the sidebar should reload.
      try {
        window.parent.postMessage({ key: "agent-refresh-loan" }, "*");
      } catch (_) {
        // not embedded
      }
      return s;

    case "error":
      s.error = event.message || "Ask Addy ran into a problem.";
      return s;

    default:
      return s; // run, reasoning, metrics, final_response (already streamed), complete
  }
}

/** The parts to show right now: finished parts plus any text still streaming. */
export const visibleParts = (s) => (s.text ? [...s.parts, { type: "text", text: s.text }] : s.parts);

const lastUserMessage = (messages) => [...messages].reverse().find((m) => m.role === "user");
const textOf = (message) =>
  (message?.content || [])
    .filter((p) => p.type === "text")
    .map((p) => p.text)
    .join("\n")
    .trim();

const RECOVER_POLL_MS = 2000;
const RECOVER_MAX_MS = 10 * 60_000;
const sleep = (ms, signal) =>
  new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener?.("abort", () => {
      clearTimeout(timer);
      resolve();
    });
  });

/**
 * The connection dropped or went silent mid-answer, but the run keeps going on the server and saves
 * its answer. Wait until it has finished, then return that answer's content (the assistant messages
 * after the last question), or null when there is none to show.
 */
export async function recoverFinishedAnswer({ threadId, identity, abortSignal, pollMs = RECOVER_POLL_MS, maxMs = RECOVER_MAX_MS }) {
  const until = Date.now() + maxMs;
  for (;;) {
    if (abortSignal?.aborted) return null;
    const run = await AskAddyV2.activeRun(threadId, identity).catch(() => ({ running: false }));
    if (!run.running || Date.now() > until) break;
    await sleep(pollMs, abortSignal);
  }
  const rows = await AskAddyV2.history(threadId, identity).catch(() => []);
  const lastQuestion = rows.map((r) => r.role).lastIndexOf("user");
  const answer = historyToMessages(rows.slice(lastQuestion + 1)).filter((m) => m.role === "assistant");
  const content = answer.flatMap((m) => (Array.isArray(m.content) ? m.content : [{ type: "text", text: String(m.content || "") }]));
  return content.some((p) => p.type !== "text" || p.text?.trim()) ? content : null;
}

const DROPPED = "The connection to Addy dropped before the answer finished. Try again.";

/** Exported for tests: this is exactly what useLocalRuntime runs. */
/**
 * What the LO asked. A question from the web app's Ask box shows its short label but asks its full prompt.
 * Files sent with no text go with no question: v2 identifies them and asks what to do. This used to send
 * "Fill in the attached files from this loan.", and the agent tried to fill finished reports the LO had
 * attached only to find out what they were.
 */
export function outgoingQuestion(user) {
  return user?.metadata?.custom?.query || textOf(user) || "";
}

export function createAdapter(ctxRef) {
  return {
    async *run({ messages, abortSignal, unstable_getMessage }) {
      const { identity, loanId, threadId, agentId, documentIds } = ctxRef.current;
      // The open Scenario Center scenario rides on the identity, like the loan on Autopilot's.
      const { scenarioId } = identity;
      const current = unstable_getMessage?.();
      const pending = current?.metadata?.custom?.pendingApproval;
      const gate = pending && current.content.find((p) => p.type === "tool-call" && p.toolCallId === pending.gateId);

      let events;
      if (gate && gate.approval && gate.approval.approved !== undefined && pending.kind === "resume") {
        // Continuation: a browser task finished (or wasn't allowed); hand the agent what happened.
        const resumeData = browserResults.get(pending.gateId) || { status: gate.approval.approved ? "failed" : "declined" };
        browserResults.delete(pending.gateId);
        events = AskAddyV2.resume({ paused: pending, resumeData, loanId, scenarioId, identity, abortSignal });
      } else if (gate && gate.approval && gate.approval.approved !== undefined) {
        // Continuation: the loan officer answered the approval card.
        events = AskAddyV2.decide({ approval: pending, approved: gate.approval.approved, loanId, scenarioId, identity, abortSignal });
      } else {
        const user = lastUserMessage(messages);
        const earlierMessages = messages.slice(0, messages.lastIndexOf(user));
        const attachments = (user?.attachments || []).map((a) => a.id).filter(Boolean);
        const question = outgoingQuestion(user);
        // v2 has no memory of a classic chat yet: its first follow-up hands the agent the old chat.
        const query = isFirstLegacyFollowUp(earlierMessages)
          ? withEarlierChat(question, earlierMessages.map((message) => ({ role: message.role, text: textOf(message) })))
          : question;
        events = AskAddyV2.query({ query, loanId, scenarioId, threadId, agentId, attachments, documentIds, identity, abortSignal });
      }

      let state = initialFold();
      // When this run began: the thinking trace's duration. Kept per run (an approval resume is its own run).
      const startedAt = Date.now();
      try {
        for await (const event of events) {
          if (event.type === "interrupted") {
            // Not an error yet: the answer is probably finished and saved on the server.
            yield { content: visibleParts(state), metadata: { custom: { pendingApproval: null, statusText: "Reconnecting…", startedAt } } };
            const recovered = await recoverFinishedAnswer({ threadId: threadId || defaultThreadId({ ...identity, loanId }), identity, abortSignal });
            if (!recovered) throw new Error(DROPPED);
            yield { content: recovered, metadata: { custom: { pendingApproval: null, statusText: "", startedAt } } };
            return;
          }
          state = foldEvent(state, event);
          if (state.error) throw new Error(state.error);
          yield {
            content: visibleParts(state),
            metadata: { custom: { pendingApproval: null, statusText: state.status, startedAt } },
          };
          if (state.paused) break; // v2 has suspended the run; it resumes when the gate is answered
        }
      } finally {
        window.dispatchEvent(new Event(THREAD_UPDATED_EVENT));
      }

      if (state.paused) {
        yield {
          content: visibleParts(state),
          status: { type: "requires-action", reason: "tool-calls" },
          metadata: { custom: { pendingApproval: state.paused, statusText: "", startedAt } },
        };
      }
    },
  };
}

export function createAttachmentAdapter(ctxRef) {
  return {
    accept: ATTACHMENT_ACCEPT,
    async add({ file }) {
      return {
        id: `local-${Date.now()}-${file.name}`,
        type: "document",
        name: file.name,
        contentType: file.type || "application/octet-stream",
        file,
        status: { type: "requires-action", reason: "composer-send" },
      };
    },
    async send(attachment) {
      const uploaded = await AskAddyV2.upload(attachment.file, ctxRef.current.identity);
      return { ...attachment, id: uploaded.uploadId, status: { type: "complete" }, content: [] };
    },
    async remove() {},
  };
}

/** v2 history -> assistant-ui ThreadMessageLike[]. Consecutive assistant rows are one turn. */
export function historyToMessages(rows) {
  const out = [];
  for (const row of rows) {
    if (row.role !== "user" && row.role !== "assistant") continue;
    if (row.role === "user") {
      // A scheduled task's run: long framing for the agent. The chat shows a small "Scheduled task"
      // label in its place (UserMessage), not a message the loan officer typed.
      const scheduled = parseScheduledHeader(row.text);
      if (scheduled) {
        out.push({
          id: row.id,
          role: "user",
          content: scheduled.name ? `Scheduled task · ${scheduled.name}` : "Scheduled task",
          ...(row.createdAt ? { createdAt: new Date(row.createdAt) } : {}),
          metadata: { custom: { scheduled } },
        });
        continue;
      }
      const { body, names } = splitAttachmentTags(row.text);
      out.push({
        id: row.id,
        role: "user",
        content: stripEarlierChat(body),
        attachments: names.length
          ? names.map((name, i) => ({
              id: `${row.id}-att-${i}`,
              type: "document",
              name,
              contentType: "",
              status: { type: "complete" },
              content: [],
            }))
          : undefined,
      });
      continue;
    }
    if (row.text?.trim() && !withoutQuietToken(row.text)) {
      // A scheduled check with nothing new: hide it, with the steps and the label of the run that said it.
      let i = out.length - 1;
      while (i >= 0 && out[i].role === "assistant") i -= 1;
      if (i >= 0 && out[i].metadata?.custom?.scheduled) out.length = i;
      continue;
    }
    if (STOPPED_NOTE.test(row.text || "")) {
      // The turn before it was stopped: show it as canceled, not the note itself.
      const prev = out[out.length - 1];
      const canceled = { type: "incomplete", reason: "cancelled" };
      if (prev && prev.role === "assistant") prev.status = canceled;
      else out.push({ id: row.id, role: "assistant", content: [{ type: "text", text: "" }], status: canceled });
      continue;
    }
    const parts = [
      // Internal plumbing (skill loading) is never shown as a step.
      ...(row.tools || []).filter((t) => t.toolName !== "skill").map((t, i) => ({
        type: "tool-call",
        toolCallId: `${row.id}-tool-${i}`,
        toolName: t.toolName,
        args: {},
        result: t.state === "result" ? true : undefined,
      })),
      ...(withoutQuietToken(row.text) ? [{ type: "text", text: withoutQuietToken(row.text) }] : []),
      ...(row.files || []).map((f) => ({
        type: "data",
        name: "v2-file",
        data: { ...f, url: AskAddyV2.absoluteUrl(f.url) },
      })),
      ...(row.sources?.length ? [{ type: "data", name: "v2-sources", data: { sources: row.sources } }] : []),
      ...(row.choice ? [{ type: "data", name: "v2-choice", data: row.choice }] : []),
      ...(row.scheduledTask ? [{ type: "data", name: "v2-scheduled-task", data: row.scheduledTask }] : []),
    ];
    // A turn still being worked on can be saved with nothing to show yet: skip it, don't render "0 steps".
    if (!parts.length) continue;
    const prev = out[out.length - 1];
    if (prev && prev.role === "assistant") prev.content.push(...parts);
    else out.push({ id: row.id, role: "assistant", content: parts, status: { type: "complete", reason: "stop" } });
  }
  return out;
}

/**
 * Earlier messages of a conversation, once, before the runtime exists. Without a threadId this is
 * the loan's (or the identity's scenario's) default conversation, or the general one.
 */
/**
 * Calls `reload` each time `pickCount` goes up after the first render: the chat list counts every
 * pick, so a click on the chat that is already open reloads it instead of doing nothing.
 */
export function useReloadOnPick(pickCount, reload) {
  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (pickCount > 0) reloadRef.current();
  }, [pickCount]);
}

export function useAskAddyV2History(identity, loanId, threadId, reloadKey = 0) {
  const [state, setState] = useState({ loading: true, messages: [], error: null, key: null });
  const key = [identity.organizationId, identity.userId, identity.scenarioId, loanId, threadId, reloadKey].join("|");
  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, messages: [], error: null, key });
    if (!identity.organizationId || !identity.userId) return undefined;
    AskAddyV2.history(threadId || defaultThreadId({ ...identity, loanId }), identity)
      .then((rows) => !cancelled && setState({ loading: false, messages: historyToMessages(rows), error: null, key }))
      .catch((error) => !cancelled && setState({ loading: false, messages: [], error: error.message, key }));
    return () => {
      cancelled = true;
    };
  }, [key]); // eslint-disable-line -- key holds every id the effect reads
  // The render that switches conversation runs before the effect: never hand it the old one's messages.
  return state.key === key ? state : { loading: true, messages: [], error: null, key };
}

const ACTIVE_RUN_POLL_MS = 5000;

/** While the conversation sits open, how often to look for a scheduled task starting to post in it. */
export const SCHEDULED_POLL_MS = 20000;

const isTabHidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

/**
 * A run working in this conversation that this view didn't start: one started earlier (before the
 * sidebar was closed or the conversation switched), or a scheduled task posting here while the
 * conversation sits open. Polls while it runs; `onFinished` fires when it ends, so the caller can
 * reload the conversation with the answer. The loan officer's own live answer is never picked up
 * here: it streams into the view already, and a reload would drop its approval cards.
 */
export function useAskAddyV2ActiveRun(identity, loanId, threadId, onFinished) {
  const [run, setRun] = useState({ running: false });
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;
  useEffect(() => {
    if (!identity.organizationId || !identity.userId) return undefined;
    const id = threadId || defaultThreadId({ ...identity, loanId });
    let cancelled = false;
    let timer;
    let wasRunning = false;
    let isFirst = true;
    const check = async () => {
      if (!isFirst && !wasRunning && isTabHidden()) {
        timer = setTimeout(check, SCHEDULED_POLL_MS);
        return;
      }
      const next = await AskAddyV2.activeRun(id, identity).catch(() => ({ running: false }));
      if (cancelled) return;
      const wasFirst = isFirst;
      isFirst = false;
      if (next.running && (wasFirst || wasRunning || next.scheduled)) {
        wasRunning = true;
        setRun(next);
        timer = setTimeout(check, ACTIVE_RUN_POLL_MS);
        return;
      }
      if (wasFirst || wasRunning) setRun({ running: false }); // nothing left over from another conversation
      if (wasRunning) {
        wasRunning = false;
        finishedRef.current?.();
      }
      timer = setTimeout(check, SCHEDULED_POLL_MS);
    };
    check();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // Keyed on the ids, not the identity object, which is rebuilt on every render.
  }, [identity.organizationId, identity.userId, identity.scenarioId, loanId, threadId]);
  return run;
}

/** The runtime. Mount it under a key per conversation (loan + thread). */
export function useAskAddyV2Runtime({ identity, loanId, threadId, agentId, documentIds, initialMessages }) {
  const ctxRef = useRef({ identity, loanId, threadId, agentId, documentIds });
  ctxRef.current = { identity, loanId, threadId, agentId, documentIds };
  const adapter = useMemo(() => createAdapter(ctxRef), []);
  const attachments = useMemo(() => createAttachmentAdapter(ctxRef), []);
  return useLocalRuntime(adapter, { initialMessages, adapters: { attachments } });
}
